/**
 * Common airline codes for validation and lookup
 */

export const AIRLINES = {
  // US Major
  'AA': { name: 'American Airlines', country: 'US' },
  'DL': { name: 'Delta Air Lines', country: 'US' },
  'UA': { name: 'United Airlines', country: 'US' },
  'WN': { name: 'Southwest Airlines', country: 'US' },
  'B6': { name: 'JetBlue Airways', country: 'US' },
  'AS': { name: 'Alaska Airlines', country: 'US' },
  'NK': { name: 'Spirit Airlines', country: 'US' },
  'F9': { name: 'Frontier Airlines', country: 'US' },
  'G4': { name: 'Allegiant Air', country: 'US' },
  'HA': { name: 'Hawaiian Airlines', country: 'US' },
  'SY': { name: 'Sun Country Airlines', country: 'US' },

  // Canada
  'AC': { name: 'Air Canada', country: 'CA' },
  'WS': { name: 'WestJet', country: 'CA' },

  // Europe
  'BA': { name: 'British Airways', country: 'UK' },
  'VS': { name: 'Virgin Atlantic', country: 'UK' },
  'AF': { name: 'Air France', country: 'FR' },
  'LH': { name: 'Lufthansa', country: 'DE' },
  'KL': { name: 'KLM Royal Dutch', country: 'NL' },
  'IB': { name: 'Iberia', country: 'ES' },
  'AZ': { name: 'ITA Airways', country: 'IT' },
  'LX': { name: 'Swiss International', country: 'CH' },
  'OS': { name: 'Austrian Airlines', country: 'AT' },
  'SK': { name: 'Scandinavian Airlines', country: 'SE' },
  'AY': { name: 'Finnair', country: 'FI' },
  'EI': { name: 'Aer Lingus', country: 'IE' },
  'TP': { name: 'TAP Air Portugal', country: 'PT' },
  'TK': { name: 'Turkish Airlines', country: 'TR' },
  'U2': { name: 'easyJet', country: 'UK' },
  'FR': { name: 'Ryanair', country: 'IE' },
  'VY': { name: 'Vueling', country: 'ES' },
  'W6': { name: 'Wizz Air', country: 'HU' },
  'SN': { name: 'Brussels Airlines', country: 'BE' },
  'LO': { name: 'LOT Polish Airlines', country: 'PL' },

  // Middle East
  'EK': { name: 'Emirates', country: 'AE' },
  'QR': { name: 'Qatar Airways', country: 'QA' },
  'EY': { name: 'Etihad Airways', country: 'AE' },
  'SV': { name: 'Saudi Arabian Airlines', country: 'SA' },
  'GF': { name: 'Gulf Air', country: 'BH' },
  'LY': { name: 'El Al Israel Airlines', country: 'IL' },
  'RJ': { name: 'Royal Jordanian', country: 'JO' },

  // Asia Pacific
  'SQ': { name: 'Singapore Airlines', country: 'SG' },
  'CX': { name: 'Cathay Pacific', country: 'HK' },
  'JL': { name: 'Japan Airlines', country: 'JP' },
  'NH': { name: 'All Nippon Airways', country: 'JP' },
  'KE': { name: 'Korean Air', country: 'KR' },
  'OZ': { name: 'Asiana Airlines', country: 'KR' },
  'CI': { name: 'China Airlines', country: 'TW' },
  'BR': { name: 'EVA Air', country: 'TW' },
  'CA': { name: 'Air China', country: 'CN' },
  'MU': { name: 'China Eastern', country: 'CN' },
  'CZ': { name: 'China Southern', country: 'CN' },
  'TG': { name: 'Thai Airways', country: 'TH' },
  'MH': { name: 'Malaysia Airlines', country: 'MY' },
  'GA': { name: 'Garuda Indonesia', country: 'ID' },
  'PR': { name: 'Philippine Airlines', country: 'PH' },
  'VN': { name: 'Vietnam Airlines', country: 'VN' },
  'AI': { name: 'Air India', country: 'IN' },

  // Oceania
  'QF': { name: 'Qantas', country: 'AU' },
  'VA': { name: 'Virgin Australia', country: 'AU' },
  'NZ': { name: 'Air New Zealand', country: 'NZ' },
  'FJ': { name: 'Fiji Airways', country: 'FJ' },

  // Latin America
  'AM': { name: 'Aeromexico', country: 'MX' },
  'LA': { name: 'LATAM Airlines', country: 'CL' },
  'AV': { name: 'Avianca', country: 'CO' },
  'CM': { name: 'Copa Airlines', country: 'PA' },
  'G3': { name: 'GOL Linhas Aereas', country: 'BR' },
  'AD': { name: 'Azul Brazilian Airlines', country: 'BR' },
  'AR': { name: 'Aerolineas Argentinas', country: 'AR' },

  // Africa
  'ET': { name: 'Ethiopian Airlines', country: 'ET' },
  'SA': { name: 'South African Airways', country: 'ZA' },
  'KQ': { name: 'Kenya Airways', country: 'KE' },
  'MS': { name: 'EgyptAir', country: 'EG' },
  'AT': { name: 'Royal Air Maroc', country: 'MA' }
};

/**
 * Get airline info by code
 */
export const getAirlineInfo = (code) => {
  if (!code) return null;
  const normalized = code.toUpperCase().trim();
  return AIRLINES[normalized] || null;
};

/**
 * Get airline name by code
 */
export const getAirlineName = (code) => {
  const info = getAirlineInfo(code);
  return info ? info.name : code;
};

/**
 * Check if a string is a valid airline code
 */
export const isValidAirlineCode = (code) => {
  if (!code || typeof code !== 'string') return false;
  const normalized = code.toUpperCase().trim();
  return /^[A-Z0-9]{2}$/.test(normalized);
};

/**
 * Extract airline code from text (looks for 2-letter codes followed by numbers)
 */
export const extractAirlineCode = (text) => {
  if (!text) return null;
  // Pattern: 2-letter code followed by digits (flight number)
  const match = text.match(/\b([A-Z]{2})\s*(\d{1,4})\b/i);
  if (match) {
    return match[1].toUpperCase();
  }
  return null;
};

/**
 * Extract flight number from text
 */
export const extractFlightNumber = (text) => {
  if (!text) return null;
  // Pattern: airline code + flight number
  const match = text.match(/\b[A-Z]{2}\s*(\d{1,4})\b/i);
  if (match) {
    return match[1];
  }
  // Pattern: just digits after "flight" or "#"
  const altMatch = text.match(/(?:flight|flt|#)\s*#?(\d{1,4})\b/i);
  if (altMatch) {
    return altMatch[1];
  }
  return null;
};

/**
 * Map common airline name variations to codes
 */
export const AIRLINE_NAME_TO_CODE = {
  'american': 'AA',
  'american airlines': 'AA',
  'delta': 'DL',
  'delta air lines': 'DL',
  'united': 'UA',
  'united airlines': 'UA',
  'southwest': 'WN',
  'southwest airlines': 'WN',
  'jetblue': 'B6',
  'alaska': 'AS',
  'alaska airlines': 'AS',
  'spirit': 'NK',
  'frontier': 'F9',
  'british airways': 'BA',
  'air france': 'AF',
  'lufthansa': 'LH',
  'klm': 'KL',
  'emirates': 'EK',
  'qatar': 'QR',
  'qatar airways': 'QR',
  'singapore': 'SQ',
  'singapore airlines': 'SQ',
  'cathay': 'CX',
  'cathay pacific': 'CX',
  'qantas': 'QF',
  'air canada': 'AC',
  'westjet': 'WS'
};

/**
 * Try to get airline code from name
 */
export const getCodeFromName = (name) => {
  if (!name) return null;
  const normalized = name.toLowerCase().trim();
  return AIRLINE_NAME_TO_CODE[normalized] || null;
};

export default AIRLINES;
